import { z } from 'zod';
import { uuidSchema } from './common.schema.js';

/**
 * The app_profile_settings `type` values as stored (UPPERCASE). Use these constants
 * everywhere the setting type is meant — retrieving, updating, creating — never raw strings.
 */
export const APP_PROFILE_SETTING_TYPE = {
  PATIENTS: 'PATIENTS',
  FEED_ORDERS: 'FEED_ORDERS',
} as const;

export const AppProfileSettingTypeEnum = z.enum([
  APP_PROFILE_SETTING_TYPE.PATIENTS,
  APP_PROFILE_SETTING_TYPE.FEED_ORDERS,
]);
export type AppProfileSettingType = z.infer<typeof AppProfileSettingTypeEnum>;

/** URL path slugs (lowercase) for each setting type — the API paths keep these. */
export const APP_PROFILE_SETTING_TYPE_SLUG = {
  [APP_PROFILE_SETTING_TYPE.PATIENTS]: 'patients',
  [APP_PROFILE_SETTING_TYPE.FEED_ORDERS]: 'feed_orders',
} as const satisfies Record<AppProfileSettingType, string>;

/** Setting type -> URL slug (e.g. 'PATIENTS' -> 'patients'). */
export function toAppProfileSettingSlug(type: AppProfileSettingType): string {
  return APP_PROFILE_SETTING_TYPE_SLUG[type];
}

/** URL slug -> setting type, case-insensitive; undefined for an unknown slug. */
export function parseAppProfileSettingSlug(slug: string): AppProfileSettingType | undefined {
  const normalized = slug.trim().toLowerCase();
  return AppProfileSettingTypeEnum.options.find(
    (type) => APP_PROFILE_SETTING_TYPE_SLUG[type] === normalized,
  );
}

/** A single configurable column: whether the field is mandatory on the form. */
const columnRuleSchema = z.object({ required: z.boolean() }).strict();

export const PatientsColumnDefinitionSchema = z
  .object({ birth_weight_grams: columnRuleSchema })
  .strict();
export type PatientsColumnDefinition = z.infer<typeof PatientsColumnDefinitionSchema>;

export const FeedOrdersColumnDefinitionSchema = z
  .object({ calorific_density: columnRuleSchema })
  .strict();
export type FeedOrdersColumnDefinition = z.infer<typeof FeedOrdersColumnDefinitionSchema>;

export const AppProfileSettingColumnDefinitionSchema = z.union([
  PatientsColumnDefinitionSchema,
  FeedOrdersColumnDefinitionSchema,
]);
export type AppProfileSettingColumnDefinition = z.infer<
  typeof AppProfileSettingColumnDefinitionSchema
>;

/** Exact columnDefinition schema for each setting type. */
export const APP_PROFILE_SETTING_DEFINITION_SCHEMAS = {
  [APP_PROFILE_SETTING_TYPE.PATIENTS]: PatientsColumnDefinitionSchema,
  [APP_PROFILE_SETTING_TYPE.FEED_ORDERS]: FeedOrdersColumnDefinitionSchema,
} as const satisfies Record<AppProfileSettingType, z.ZodType>;

/**
 * Default columnDefinition for each setting type — the single source of truth for what applies
 * when a hospital has not configured (or has deactivated / corrupted) the setting: the seeded
 * rows, the server fallback, the UI before the flag loads, and the Profile Settings page.
 * Birth weight is optional by default; caloric density is required by default.
 */
export const APP_PROFILE_SETTING_DEFAULTS = {
  [APP_PROFILE_SETTING_TYPE.PATIENTS]: { birth_weight_grams: { required: false } },
  [APP_PROFILE_SETTING_TYPE.FEED_ORDERS]: { calorific_density: { required: true } },
} as const satisfies {
  [APP_PROFILE_SETTING_TYPE.PATIENTS]: PatientsColumnDefinition;
  [APP_PROFILE_SETTING_TYPE.FEED_ORDERS]: FeedOrdersColumnDefinition;
};

/** Default "Is Required" value of one field; `true` when the field has no known default. */
export function getAppProfileSettingFieldDefault(type: AppProfileSettingType, field: string): boolean {
  const defaults = APP_PROFILE_SETTING_DEFAULTS[type] as Record<string, { required: boolean } | undefined>;
  return defaults[field]?.required ?? true;
}

/**
 * Route body. The route cannot see :type, so the service re-validates
 * columnDefinition against APP_PROFILE_SETTING_DEFINITION_SCHEMAS[type].
 */
export const UpsertAppProfileSettingBodySchema = z
  .object({ columnDefinition: AppProfileSettingColumnDefinitionSchema })
  .strict();
export type UpsertAppProfileSettingBody = z.infer<typeof UpsertAppProfileSettingBodySchema>;

export const AppProfileSettingResponseSchema = z.object({
  id: uuidSchema,
  hospital_id: uuidSchema,
  type: AppProfileSettingTypeEnum,
  columnDefinition: AppProfileSettingColumnDefinitionSchema,
  is_active: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type AppProfileSettingResponse = z.infer<typeof AppProfileSettingResponseSchema>;

/** Fields whose "Is Required" rule can be configured, per setting type. */
export const APP_PROFILE_SETTING_FIELDS = {
  [APP_PROFILE_SETTING_TYPE.PATIENTS]: ['birth_weight_grams'],
  [APP_PROFILE_SETTING_TYPE.FEED_ORDERS]: ['calorific_density'],
} as const satisfies Record<AppProfileSettingType, readonly string[]>;

/**
 * Effective requirements for a hospital (GET /app-profile-settings/:type/effective):
 * `true` = required. Missing / inactive / malformed settings resolve to `true`.
 */
export const PatientsEffectiveRequirementsSchema = z
  .object({ birth_weight_grams: z.boolean() })
  .strict();
export type PatientsEffectiveRequirements = z.infer<typeof PatientsEffectiveRequirementsSchema>;

export const FeedOrdersEffectiveRequirementsSchema = z
  .object({ calorific_density: z.boolean() })
  .strict();
export type FeedOrdersEffectiveRequirements = z.infer<typeof FeedOrdersEffectiveRequirementsSchema>;

export const AppProfileSettingEffectiveResponseSchema = z.union([
  PatientsEffectiveRequirementsSchema,
  FeedOrdersEffectiveRequirementsSchema,
]);
export type AppProfileSettingEffectiveResponse = z.infer<
  typeof AppProfileSettingEffectiveResponseSchema
>;
